import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import { Check, Download, Edit, Eye, MoreHorizontal, Pencil, X } from 'lucide-react';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "../components/ui/pagination";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { useToast } from '../components/ui/use-toast';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { app } from "../firebase";
import { getAuth } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  onSnapshot,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";

const Certificates = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewCertificate, setViewCertificate] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editCertificate, setEditCertificate] = useState(null);
  const db = getFirestore(app);
  const auth = getAuth(app);
  const [previewMode, setPreviewMode] = useState(false);


  // Sample certificate data
  const [certificateData, setCertificateData] = useState([]);

  const imageUrls = [
    'https://i.pravatar.cc/150?img=1',
    'https://i.pravatar.cc/150?img=2',
    'https://i.pravatar.cc/150?img=3',
    'https://i.pravatar.cc/150?img=4',
    'https://i.pravatar.cc/150?img=5',
    'https://i.pravatar.cc/150?img=6',
    'https://i.pravatar.cc/150?img=7',
    'https://i.pravatar.cc/150?img=8',
    'https://i.pravatar.cc/150?img=9',
    'https://i.pravatar.cc/150?img=10',
    'https://i.pravatar.cc/150?img=11',
    'https://i.pravatar.cc/150?img=12',
    'https://i.pravatar.cc/150?img=13',
    'https://i.pravatar.cc/150?img=14',
    'https://i.pravatar.cc/150?img=15'
  ]


  function getRandomImage() {
    const randomIndex = Math.floor(Math.random() * imageUrls.length); // Generate a random index  
    return imageUrls[randomIndex]; // Return the image at that index  
  }

  useEffect(() => {
    const certificatesUnsubscribe = onSnapshot(
      collection(db, "certifiedStudents"),
      (querySnapshot) => {
        //   setLoader(true); // Show loader while fetching data
        const result = querySnapshot.docs.map((doc) => ({
          id: doc.id, // Get the document ID  
          ...doc.data(), // Spread the document data  
        }));
        console.log(result)
        setCertificateData(result);
        //   setLoader(false); // Hide loader after fetching data
      },
      (error) => {
        //  addToast(error.message, "error"); // Handle errors
        //  setLoader(false); // Hide loader in case of error
      }
    );
    return () => {
      certificatesUnsubscribe();
    }
  }, [])

  const getStatusColor = (status) => {
    switch (status) {
      case 'Issued':
        return 'text-green-600 bg-green-50 border border-green-200';
      case 'Pending':
        return 'text-yellow-600 bg-yellow-50 border border-yellow-200';
      case 'Canceled':
        return 'text-red-600 bg-red-50 border border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border border-gray-200';
    }
  };

  const handleViewCertificate = (cert) => {
    setViewCertificate(cert);
    setPreviewMode(true);
  };

  const handleEditCertificate = (cert) => {
    navigate(`/certificate/edit/${cert?.id}`)
  };

  const handleSaveEdit = () => {
    setCertificateData(
      certificateData.map(cert =>
        cert.certificateId === editCertificate.certificateId ? editCertificate : cert
      )
    );

    setIsEditOpen(false);
    toast({
      title: "Certificate Updated",
      description: `Certificate details for ${editCertificate.studentsName} have been updated.`,
    });
  };
  const handleCertificateDownload = async (cert) => {
    const { certificate } = cert;
    if (!certificate?.url || certificate?.url == "") {
      toast({
        title: "Error fetching course data",
        description: "File Not Exists",
        variant: "destructive",
      });
      return;
    }
    const fileUrl = certificate?.url;

    try {
      const response = await axios.get(fileUrl, {
        responseType: 'blob',
        onDownloadProgress: (event) => {
          if (event.total) {
            const percentDone = Math.round((100 * event.loaded) / event.total);
          }
        }
      });

      const file = new Blob([response.data], { type: 'application/pdf' });
      saveAs(file, fileUrl.includes('assets') ? fileUrl.split('/').pop() : certificate?.fileName); // Save the file with the last part of the URL  
    } catch (error) {
      console.error('Download error:', error);
      //setProgress(0); // Reset progress on error  
    } finally {
      // setIsLoading(false);
    }
  };

  const filteredData = certificateData.filter(cert =>
    cert.studentsName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.emailId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.courseName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.certificateId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Certified Student List" subtitle="Manage student certificates" />

      <div className="flex-1 p-8 animate-fade-in">
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 flex justify-between items-center">
            <div className="flex-1 max-w-md">
              <Input
                placeholder="Search by name, email or course..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-gray-300"
              />
            </div>
            <Button
              onClick={() => navigate('/certificates/generate')}
              className="bg-bytesplash-blue text-white hover:bg-bytesplash-blue/90"
            >
              Generate Certificate
            </Button>
          </div>

          <div className="px-6 pb-6 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200">
                  <TableHead className="text-sm text-gray-600 font-medium py-3">Students</TableHead>
                  <TableHead className="text-sm text-gray-600 font-medium py-3">Course</TableHead>
                  <TableHead className="text-sm text-gray-600 font-medium py-3">Certificate ID</TableHead>
                  <TableHead className="text-sm text-gray-600 font-medium py-3">Status</TableHead>
                  <TableHead className="text-sm text-gray-600 font-medium py-3 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((cert, index) => (
                  <TableRow key={cert.certificateId} className="border-b border-gray-100 hover:bg-gray-50">
                    <TableCell className="flex items-center gap-3 py-3">
                      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                        <img
                          src={getRandomImage()}
                          alt={cert.studentsName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <div className="font-medium text-sm">{cert.studentsName}</div>
                        <div className="text-xs text-gray-500">{cert.emailId}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm py-3">{cert.courseName}</TableCell>
                    <TableCell className="text-sm py-3">{cert.certificateId}</TableCell>
                    <TableCell className="text-sm py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(cert.status)}`}>
                        {cert.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-center py-2">
                      <div className="flex justify-center space-x-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                          onClick={() => handleViewCertificate(cert)}
                        >
                          <Eye size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                          onClick={() => handleEditCertificate(cert)}
                        >
                          <Pencil size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full"
                          onClick={() => handleCertificateDownload(cert)}
                        >
                          <Download size={18} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="p-4 border-t border-gray-200">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious href="#" />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#" isActive>1</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#">2</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#">3</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#">4</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#">5</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext href="#" />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      </div>

      {previewMode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg max-w-3xl w-full h-[95vh] overflow-auto">
            <div className="flex justify-between mb-1">
              <h2 className="text-xl font-bold text-primary">Certificate Preview</h2>
              <Button variant="ghost" onClick={() => setPreviewMode(false)}>
                <X size={24} /> {/* Use the X icon for closing */}

              </Button>
            </div>
            <div className="border p-0 rounded-lg">
              <div className="text-center space-y-0">
                {/* Check if the certificate URL exists */}
                <div className="text-center space-y-0">
                  {/* Check if the certificate URL exists */}
                  {viewCertificate['certificate']?.url && (
                    <div className="my-4">
                      {/* Check the file type based on the URL extension */}
                      {viewCertificate['certificate'].url.includes('.pdf') ? (
                        // Display PDF if the URL ends with .pdf  
                        <embed
                          src={viewCertificate['certificate'].url}
                          type="application/pdf"
                          width="100%"
                          height="500px"
                          className="border border-gray-200 rounded-lg"
                          aria-label="Uploaded Certificate PDF"
                        />
                      ) : (
                        // Display image if not a PDF  
                        <img
                          src={viewCertificate['certificate'].url}
                          alt="Uploaded Certificate"
                          className="max-w-full h-auto border border-gray-200 rounded-lg"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Certificate Sheet */}
      <Sheet open={isEditOpen} onOpenChange={setIsEditOpen}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Edit Certificate</SheetTitle>
            <SheetDescription>
              Update certificate information for {editCertificate?.studentsName}
            </SheetDescription>
          </SheetHeader>

          {editCertificate && (
            <div className="py-6">
              <div className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="student">Student Name</Label>
                  <Input
                    id="student"
                    value={editCertificate.studentsName}
                    onChange={(e) => setEditCertificate({ ...editCertificate, studentsName: e.target.value })}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={editCertificate.emailId}
                    onChange={(e) => setEditCertificate({ ...editCertificate, emailId: e.target.value })}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="course">Course</Label>
                  <Input
                    id="course"
                    value={editCertificate.courseName}
                    onChange={(e) => setEditCertificate({ ...editCertificate, courseName: e.target.value })}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={editCertificate.status}
                    onValueChange={(value) => setEditCertificate({ ...editCertificate, status: value })}
                  >
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Issued">Issued</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Canceled">Canceled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="issuedDate">Issue Date</Label>
                  <Input
                    id="issuedDate"
                    type="date"
                    value={editCertificate.issuedDate}
                    onChange={(e) => setEditCertificate({ ...editCertificate, issuedDate: e.target.value })}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="expiryDate">Expiry Date</Label>
                  <Input
                    id="expiryDate"
                    type="date"
                    value={editCertificate.expiryDate}
                    onChange={(e) => setEditCertificate({ ...editCertificate, expiryDate: e.target.value })}
                  />
                </div>
              </div>

              <SheetFooter className="mt-6">
                <div className="flex justify-between w-full">
                  <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                    <X className="mr-1 h-4 w-4" />
                    Cancel
                  </Button>
                  <Button onClick={handleSaveEdit}>
                    <Check className="mr-1 h-4 w-4" />
                    Save Changes
                  </Button>
                </div>
              </SheetFooter>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Certificates;
